import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useTabState } from '../../hooks/useTabState';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import SearchLeadershipTeam from '../../components/SearchLeadershipTeam';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';
import DisplayDate from '../../components/DisplayDate';

// TYPES
import {
	LeadershipTeamMembersLeadershipTeamMember,
	LeadershipTeamSearchUserSearchSummary,
	LeadershipTeamAssignCommand,
	LeadershipTeamMembersLeadershipTeamMemberResult
} from '../../api/models';
type TabStatus = 'current' | 'past';

const LeadershipTeam: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control active tab and content
	const [activeTab, setActiveTab] = useTabState<TabStatus>('current', 'tabView');

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Member Id
	const [memberDetail, setMemberDetail] = React.useState<LeadershipTeamMembersLeadershipTeamMember>({});

	// Set up Data state
	const [leadershipTeamData, setLeadershipTeamData] = React.useState<LeadershipTeamMembersLeadershipTeamMemberResult>(
		{}
	);

	// function to  Get leadership team data from api call
	const getLeadershipTeamData = async () => {
		setIsLoading(true);

		const response = await apibridge.getLeadershipTeamData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = {} } = response;

			setIsLoading(false);

			setLeadershipTeamData(result);
		}
	};

	React.useEffect(() => {
		getLeadershipTeamData();
	}, []);

	const { currentLeadershipTeamMembers = [], pastLeadershipTeamMembers = [] } = leadershipTeamData;

	// Remove Leadership from Team
	const removeMemberHandler = async (item: LeadershipTeamMembersLeadershipTeamMember) => {
		setMemberDetail(item);

		setShowRemoveModal(true);
	};

	// Add Leadership to Team
	const addLeadershipHandler = async (item: LeadershipTeamSearchUserSearchSummary) => {
		if (item !== undefined && item.id !== undefined) {
			setIsLoading(true);

			const data: LeadershipTeamAssignCommand = { userId: item.id };

			const response = await apibridge.addLeadershipTeamMember(data);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				getLeadershipTeamData();
			}
		}
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<nav className="page-header-section mbtom-30">
									<div className="header-left">
										<Button
											className={`staff-category pdl-0 ${activeTab === 'current' ? 'active' : ''}`}
											onClick={() => setActiveTab('current')}
										>
											Current
										</Button>

										{isOrganisationAdmin && (
											<Button
												className={`staff-category ${activeTab === 'past' ? 'active' : ''}`}
												onClick={() => setActiveTab('past')}
											>
												Past
											</Button>
										)}
									</div>
								</nav>

								{activeTab === 'current' ? (
									<>
										{currentLeadershipTeamMembers && currentLeadershipTeamMembers.length > 0 ? (
											<section className="info-section mtop-0">
												<ul>
													{currentLeadershipTeamMembers.map((item) => {
														return (
															<li key={item.userId} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0">
																		<CircleImage
																			firstName={item.firstName}
																			lastName={item.lastName}
																			src={item.profileImage}
																		/>
																	</div>

																	<div className="col-md-auto full-width">
																		<div className="row detail-info center">
																			<div className="col-lg-4 detail-name">
																				{item.firstName} {item.lastName}
																				&nbsp;
																				{item.isCurrentUser && <strong>(You)</strong>}
																			</div>

																			<div className="col-lg-8 detail-status">
																				<span className="label">{item.position}</span>
																			</div>
																		</div>
																	</div>

																	<div className="col-auto pdr-0 link-group wide">
																		{isOrganisationAdmin ? (
																			<>
																				{isOrganisationActive && (
																					<Button onClick={() => removeMemberHandler(item)} className="btn-text remove">
																						Remove from team
																					</Button>
																				)}

																				<Link className="btn-text pdr-0" to={`/staff/${item.userProfileId}`}>
																					View
																				</Link>
																			</>
																		) : null}
																	</div>
																</div>
															</li>
														);
													})}
												</ul>
											</section>
										) : (
											<div className="detail-prompt">You have not yet added any Leadership Team Members.</div>
										)}

										{isOrganisationAdmin && isOrganisationActive ? (
											<section className="people-search-wrapper">
												<h2 className="search-heading">Add a member</h2>

												<SearchLeadershipTeam
													placeholder="Begin typing a staff name..."
													onChange={(item: LeadershipTeamSearchUserSearchSummary) => addLeadershipHandler(item)}
												/>
											</section>
										) : null}
									</>
								) : activeTab === 'past' ? (
									<>
										{pastLeadershipTeamMembers && pastLeadershipTeamMembers.length > 0 ? (
											<section className="info-section mtop-0">
												<ul>
													{pastLeadershipTeamMembers.map((item, index) => {
														return (
															<li key={`past-staff-${item.userId}-${index}`} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0">
																		<CircleImage
																			firstName={item.firstName}
																			lastName={item.lastName}
																			src={item.profileImage}
																		/>
																	</div>

																	<div className="col-md-auto full-width">
																		<div className="row detail-info center">
																			<div className="col-lg-3 detail-name">
																				{item.firstName} {item.lastName}
																				&nbsp;
																				{item.isCurrentUser && <strong>(You)</strong>}
																			</div>

																			<div className="col-lg-3 detail-status">
																				<span className="label">{item.position}</span>
																			</div>

																			{item.endDate ? (
																				<div className="col-lg-6">
																					<div className="row">
																						<div className="col-lg-6 pl-0">
																							<span className="body-small text-grey-4 fw-bold mr-1">Start</span>

																							<span className="fw-medium">
																								<DisplayDate>{item?.startDate}</DisplayDate>
																							</span>
																						</div>

																						<div className="col-6 pl-0">
																							<span className="body-small text-grey-4 fw-bold mr-1">End</span>

																							<span className="fw-medium">
																								<DisplayDate>{item?.endDate}</DisplayDate>
																							</span>
																						</div>
																					</div>
																				</div>
																			) : null}
																		</div>
																	</div>

																	<div className="col-auto pdr-0 link-group wide">
																		<Link
																			className="btn-text pdr-0"
																			to={`/${item.isCurrentMember ? 'staff' : 'past-staff'}/${item.userProfileId}`}
																		>
																			View
																		</Link>
																	</div>
																</div>
															</li>
														);
													})}
												</ul>
											</section>
										) : null}
									</>
								) : null}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove Leadership team member">
				<RemoveModal memberDetail={memberDetail} getLeadershipTeamData={getLeadershipTeamData} />
			</GlobalModal>
		</div>
	);
};

export default LeadershipTeam;
